import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  Button,
  Chip,
  DisplayText,
  IconLink,
  TextField,
  theme,
  useFluidSize,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { DeliveryPreferenceType } from '../../graphql/types';

export const DeliveryPreferencesForm = ({
  preferences,
  submitting,
  onClose,
  onSave,
}: DeliveryPreferencesFormProps) => {
  const { match } = useResponsive();

  const [localType, setLocalType] = useState<string>(preferences.type ?? '');
  const [localNotes, setLocalNotes] = useState<string>(preferences.notes ?? '');

  const submitForm = useCallback(() => {
    onSave({
      type: localType as DeliveryPreferenceType,
      notes: localNotes,
    });
  }, [localNotes, localType, onSave]);

  return (
    <SafeAreaView style={match([styles.formXs, styles.formSm])}>
      <Header onClose={onClose} />
      <TypeSelector localType={localType} setLocalType={setLocalType} />
      <NotesField localNotes={localNotes} setLocalNotes={setLocalNotes} />
      <SubmitButton submitting={submitting} submitForm={submitForm} />
    </SafeAreaView>
  );
};

const Header = ({ onClose }: Partial<DeliveryPreferencesFormProps>) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const { isXS } = currentBreakpoint;

  return (
    <View style={isXS ? styles.column : styles.row}>
      {!isXS && (
        <DisplayText size={3}>{t('delivery.preferences.title')}</DisplayText>
      )}
      <View style={styles.icon}>
        <IconLink
          testID="delivery.preferences.close-button"
          name={isXS ? 'IconArrowLeft' : 'IconClose'}
          iconSize={24}
          accessibilityRole="button"
          accessibilityLabel={t('general.close')}
          accessibilityHint={t('general.close')}
          onPress={onClose}
        />
      </View>
      {isXS ? (
        <DisplayText style={styles.header} size={3}>
          {t('delivery.preferences.title')}
        </DisplayText>
      ) : null}
    </View>
  );
};

const TypeSelector = ({
  localType,
  setLocalType,
}: {
  readonly localType: string;
  readonly setLocalType: (type: string) => void;
}) => {
  const { t } = useLocalizationContext();
  const fluid = useFluidSize({ min: 375, max: 425 });
  const paddingHorizontal = fluid(12, 16);
  const fluidPadding = useStyle(
    () => ({ paddingHorizontal }),
    [paddingHorizontal],
  );

  return (
    <View>
      <BodyText size={5} style={styles.sectionLabel}>
        {t('delivery.preferences.dropoff-label')}
      </BodyText>
      <Chip.Menu value={localType} onChange={setLocalType}>
        <Chip
          value={DeliveryPreferenceType.LeaveAtDoor}
          testID="delivery.preferences.form.leave-at-door"
          height={48}
          accessibilityHint={t('delivery.preferences.leave-at-door')}
          accessibilityLabel={t('delivery.preferences.leave-at-door')}
          style={fluidPadding}
        >
          {t('delivery.preferences.leave-at-door')}
        </Chip>
        <Chip
          value={DeliveryPreferenceType.Door}
          testID="delivery.preferences.form.meet-at-door"
          height={48}
          accessibilityHint={t('delivery.preferences.meet-at-door')}
          accessibilityLabel={t('delivery.preferences.meet-at-door')}
          style={fluidPadding}
        >
          {t('delivery.preferences.meet-at-door')}
        </Chip>
        <Chip
          value={DeliveryPreferenceType.Curbside}
          testID="delivery.preferences.form.meet-outside"
          height={48}
          accessibilityHint={t('delivery.preferences.meet-outside')}
          accessibilityLabel={t('delivery.preferences.meet-outside')}
          style={fluidPadding}
        >
          {t('delivery.preferences.meet-outside')}
        </Chip>
      </Chip.Menu>
    </View>
  );
};

const NotesField = ({
  localNotes,
  setLocalNotes,
}: {
  readonly localNotes: string;
  readonly setLocalNotes: (notes: string) => void;
}) => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.flex}>
      <View style={[styles.row, styles.sectionLabel]}>
        <BodyText size={5}>{t('delivery.preferences.note-label')}</BodyText>
        <BodyText
          size={5}
          style={localNotes.length === 128 && styles.cautionText}
        >
          {localNotes.length}/{128}
        </BodyText>
      </View>
      <TextField
        testID="delivery.preferences.notes"
        style={styles.inputField}
        value={localNotes}
        multiline
        maxLength={128}
        placeholder={t('delivery.preferences.note-placeholder')}
        accessibilityLabel={t('delivery.preferences.note-hint')}
        accessibilityHint={t('delivery.preferences.note-hint')}
        onChangeText={setLocalNotes}
      />
    </View>
  );
};

const SubmitButton = ({ submitting, submitForm }: SubmitButtonProps) => {
  const { t } = useLocalizationContext();

  return (
    <Button
      size="large"
      width="100%"
      onPress={submitForm}
      isLoading={submitting}
      testID="delivery.preferences.save-button"
      accessibilityLabel={t('delivery.preferences.save.a11y')}
      accessibilityHint={t('delivery.preferences.save.a11y')}
      accessibilityRole="button"
    >
      {t('delivery.preferences.save')}
    </Button>
  );
};

type SubmitButtonProps = {
  readonly submitting?: boolean;
  readonly submitForm: () => void;
};

type DeliveryPreferences = {
  readonly type: DeliveryPreferenceType;
  readonly notes?: string;
};

type DeliveryPreferencesFormProps = {
  readonly preferences: DeliveryPreferences;
  readonly submitting?: boolean;
  readonly onClose: () => void;
  readonly onSave: (preferences: DeliveryPreferences) => void;
};

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  formSm: {
    flex: 1,
    padding: theme.spacing['6'],
  },
  formXs: {
    flex: 1,
    padding: theme.spacing['4'],
  },
  icon: {
    width: 24,
    height: 24,
  },
  header: {
    marginTop: theme.spacing['6'],
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  column: {
    flexDirection: 'column',
  },
  sectionLabel: {
    marginTop: theme.spacing['10'],
    marginBottom: theme.spacing['2'],
  },
  inputField: {
    height: 80,
  },
  cautionText: {
    color: theme.colors.CAUTION,
  },
});
